import React, { useState, useEffect } from 'react';
import { authAPI } from '../services/api';
import { LEGAL_VERSION } from '../legal/content';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../ToastContext';
import { COUNTRIES, getCurrencyForCountry } from '../data/countries';
import { APP_VERSION } from '../version';
import './LandingPage.css';

export default function LandingPage() {
  const { login } = useAuth();
  const { toast } = useToast();
  const [modal, setModal] = useState(null); // null | 'login' | 'register' | 'forgot' | 'reset'
  const [loginMethod, setLoginMethod] = useState('phone'); // 'phone' | 'email'
  const [loading, setLoading] = useState(false);
  const [billing, setBilling] = useState('monthly'); // 'monthly' | 'annual'
  const [form, setForm] = useState({
    name: '', email: '', password: '', confirm_password: '', business_name: '', company_name: '',
    country_code: 'BW', phone_number: '', initial_capital: '',
    reset_code: '', new_password: '',
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Launched from the home screen (installed PWA)? Jump straight to the login
  // pop-up, bypassing the landing page. Cancelling the modal reveals the
  // landing page underneath, so users can still get back to it.
  useEffect(() => {
    const isStandalone =
      window.matchMedia?.('(display-mode: standalone)').matches ||
      window.navigator.standalone === true; // iOS Safari home-screen apps
    if (isStandalone) setModal('login');
  }, []);

  const selectedCountry = COUNTRIES.find((c) => c.code === form.country_code) || COUNTRIES[0];

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const setCountry = (e) => {
    const code = e.target.value;
    setForm(f => ({ ...f, country_code: code, phone_number: '' }));
  };

  // Full international number as bare digits, matching the backend's canonical
  // phone form (leading zero of the local part dropped, e.g. +267 071… → 26771…).
  const fullPhone = () =>
    `${selectedCountry.dial}${form.phone_number.replace(/^0/, '')}`.replace(/\D/g, '');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const identifier = loginMethod === 'email' ? form.email : fullPhone();
      const { data } = await authAPI.login({ identifier, password: form.password });
      if (data.success) await login(data.user, data.business, data.parentCompany, data.billing, data.legal);
      else toast(data.error || 'Login failed', 'error');
    } catch (err) {
      toast(err.response?.data?.error || 'Login failed', 'error');
    } finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm_password) {
      toast('Passwords do not match', 'error');
      return;
    }
    if (!acceptedTerms) {
      toast('Please read and accept the Terms of Service and Privacy Policy', 'error');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        accept_terms: true,
        terms_version: LEGAL_VERSION,
        name: form.name,
        email: form.email || undefined,
        password: form.password,
        business_name: form.business_name,
        company_name: form.company_name || undefined,
        country_code: form.country_code,
        currency: getCurrencyForCountry(form.country_code),
        phone: form.phone_number ? fullPhone() : '',
        initial_capital: form.initial_capital ? parseFloat(form.initial_capital) : 0,
      };
      const { data } = await authAPI.register(payload);
      if (data.success) {
        // Registration no longer auto-logs in (enumeration-safe: the response is
        // identical whether or not the identifier was already taken). Send the
        // user to sign in with the credentials they just chose — prefilled.
        toast(data.message || 'Your account is ready — please sign in.', 'success');
        setForm(f => ({ ...f, password: '' }));
        setLoginMethod(form.email ? 'email' : 'phone');
        setModal('login');
      } else toast(data.error || 'Registration failed', 'error');
    } catch (err) {
      toast(err.response?.data?.error || 'Registration failed', 'error');
    } finally { setLoading(false); }
  };

  const resetIdentifier = () => (loginMethod === 'email' ? form.email : fullPhone());

  const handleForgot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await authAPI.forgotPassword({ identifier: resetIdentifier() });
      // Response is intentionally the same whether or not the account exists.
      toast(data.message || 'If that account exists, a reset code has been sent.', 'success');
      setForm(f => ({ ...f, reset_code: '', new_password: '' }));
      setModal('reset');
    } catch (err) {
      toast(err.response?.data?.error || 'Could not send reset code', 'error');
    } finally { setLoading(false); }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await authAPI.resetPassword({
        identifier: resetIdentifier(),
        code: form.reset_code,
        newPassword: form.new_password,
      });
      if (data.success) {
        toast('Password reset. Please sign in with your new password.', 'success');
        setForm(f => ({ ...f, password: '', reset_code: '', new_password: '' }));
        setModal('login');
      } else toast(data.error || 'Could not reset password', 'error');
    } catch (err) {
      toast(err.response?.data?.error || 'Could not reset password', 'error');
    } finally { setLoading(false); }
  };

  // Mobile number or email — the same field signs in and requests a reset code,
  // so a phone-only owner can reset by SMS the same way they sign in.
  const identifierField = (
    loginMethod === 'phone' ? (
      <div className="form-group">
        <label>Mobile number</label>
        <div className="lp-phone-row">
          <select
            value={form.country_code}
            onChange={(e) => setForm(f => ({ ...f, country_code: e.target.value }))}
            style={{ width: '110px', flexShrink: 0 }}
            aria-label="Country dial code"
          >
            {COUNTRIES.map((c) => (
              <option key={c.code} value={c.code}>{c.flag} {c.dial}</option>
            ))}
          </select>
          <input
            type="tel"
            value={form.phone_number}
            onChange={set('phone_number')}
            required
            autoComplete="tel-national"
            placeholder="71 234 567"
          />
        </div>
        <p className="lp-modal-switch" style={{ textAlign: 'left', marginTop: '0.35rem' }}>
          <button type="button" className="lp-link" onClick={() => setLoginMethod('email')}>Use email instead</button>
        </p>
      </div>
    ) : (
      <div className="form-group">
        <label>Email</label>
        <input type="email" value={form.email} onChange={set('email')} required autoComplete="email" placeholder="you@example.com" />
        <p className="lp-modal-switch" style={{ textAlign: 'left', marginTop: '0.35rem' }}>
          <button type="button" className="lp-link" onClick={() => setLoginMethod('phone')}>Use mobile number instead</button>
        </p>
      </div>
    )
  );

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  // Mirrors backend/src/config/plans.js (annual = 11 months → ~1 month free).
  const PRICING = {
    starter:    { monthly: 50,  annual: 550,  usd: { monthly: '3.99',  annual: '43.89'  } },
    pro:        { monthly: 150, annual: 1650, usd: { monthly: '9.99',  annual: '109.89' } },
    enterprise: { monthly: 250, annual: 2750, usd: { monthly: '14.99', annual: '164.89' } },
  };
  // 14-day free trial: new sign-ups only — every account starts on a Starter trial.
  // Pro/Enterprise (reached by upgrading) never include one.
  const hasTrial = (key) => key === 'starter';
  const money = (n) => n.toLocaleString('en-US');
  const perMonth = (annual) => (annual / 12).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Price headline + savings line + trial line + USD tagline for a tier, driven by the toggle.
  const priceBlock = (key, taglineSuffix) => {
    const p = PRICING[key];
    const annual = billing === 'annual';
    const saving = p.monthly * 12 - p.annual;
    return (
      <>
        <div className="lp-price-amount">
          P{annual ? money(p.annual) : money(p.monthly)}
          <span className="lp-price-per">{annual ? '/year' : '/month'}</span>
        </div>
        {annual && (
          <div className="lp-price-save-line">Save P{money(saving)} a year · ≈ P{perMonth(p.annual)}/mo</div>
        )}
        {hasTrial(key) && (
          <div className="lp-price-save-line">First 2 weeks free</div>
        )}
        <p className="lp-price-tagline">
          ≈ ${annual ? `${p.usd.annual} USD/yr` : `${p.usd.monthly} USD/mo`} · {taglineSuffix}
        </p>
      </>
    );
  };

  return (
    <div className="lp">

      {/* ── NAV ────────────────────────────────────────────────────────── */}
      <nav className="lp-nav">
        <div className="lp-nav-inner">
          <span className="lp-nav-logo">
            <img src="/emblem-192.png" className="lp-logo-icon" alt="ShopBook logo" />
            ShopBook
          </span>
          <div className="lp-nav-links">
            <button onClick={() => scrollTo('features')}>Features</button>
            <button onClick={() => scrollTo('how')}>How it Works</button>
            <button onClick={() => scrollTo('pricing')}>Pricing</button>
          </div>
          <div className="lp-nav-ctas">
            <button className="lp-btn-ghost" onClick={() => setModal('login')}>Sign In</button>
            <button className="lp-btn-primary" onClick={() => setModal('register')}>Get Started Free</button>
          </div>
        </div>
      </nav>

      {/* ── HERO ───────────────────────────────────────────────────────── */}
      <section className="lp-hero">
        <div className="lp-hero-text">
          <div className="lp-badge">Built for patchy connections</div>
          <h1>
            Run your shop<br />
            <span className="lp-accent">without the headaches</span>
          </h1>
          <p className="lp-hero-sub">
            Track stock, record sales, and manage customer debts — and if your signal drops mid-sale, it's kept and sent when you're back online.
            Built for small businesses that can't afford downtime.
          </p>
          <div className="lp-hero-ctas">
            <button className="lp-btn-primary lp-btn-lg" onClick={() => setModal('register')}>
              Start Free Trial
            </button>
            <button className="lp-btn-outline lp-btn-lg" onClick={() => scrollTo('how')}>
              See How it Works
            </button>
          </div>
          <p className="lp-hero-note">No credit card · 2-week free trial on every new account</p>
        </div>

        <div className="lp-hero-visual">
          <div className="lp-mockup">
            <div className="lp-mockup-topbar">
              <div className="lp-mockup-dots"><span/><span/><span/></div>
              <div className="lp-mockup-title">ShopBook Dashboard</div>
            </div>
            <div className="lp-mockup-body">
              <div className="lp-mock-stats-row">
                <div className="lp-mock-stat">
                  <div className="lp-mock-label">Today's Sales</div>
                  <div className="lp-mock-val green">2,450.00</div>
                </div>
                <div className="lp-mock-stat">
                  <div className="lp-mock-label">Outstanding Debt</div>
                  <div className="lp-mock-val red">890.00</div>
                </div>
              </div>
              <div className="lp-mock-divider"/>
              <div className="lp-mock-row-label">Recent Sales</div>
              {[
                { name: 'Mealie Meal 5kg', qty: 3, amt: '150.00', status: 'paid' },
                { name: 'Cooking Oil 2L',  qty: 2, amt: '96.00',  status: 'debt' },
                { name: 'Sugar 2kg',       qty: 5, amt: '175.00', status: 'partial' },
              ].map(({ name, qty, amt, status }) => (
                <div key={name} className="lp-mock-item">
                  <span className="lp-mock-item-name">{name} ×{qty}</span>
                  <span className="lp-mock-item-right">
                    <span className={`lp-mock-badge ${status}`}>{status}</span>
                    <span className="lp-mock-amt">{amt}</span>
                  </span>
                </div>
              ))}
              <div className="lp-mock-sync">
                <span className="lp-mock-sync-dot"/>
                Synced · 2 devices
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── TRUST STRIP ────────────────────────────────────────────────── */}
      <div className="lp-trust">
        {[
          ['📶', 'Patchy signal?', 'Sales save & sync later'],
          ['🔄', 'Multi-device sync', 'Works across phones'],
          ['⭐', 'Debt ratings', 'Know who pays on time'],
          ['👥', 'Team access', 'Role-based permissions'],
        ].map(([icon, title, sub]) => (
          <div key={title} className="lp-trust-item">
            <span className="lp-trust-icon">{icon}</span>
            <div>
              <strong>{title}</strong>
              <span>{sub}</span>
            </div>
          </div>
        ))}
      </div>

      {/* ── FEATURES ───────────────────────────────────────────────────── */}
      <section className="lp-section" id="features">
        <div className="lp-section-inner">
          <div className="lp-section-label">Features</div>
          <h2>Everything your shop needs, nothing it doesn't</h2>
          <p className="lp-section-sub">
            No accounting degree. No complex setup. Just open the app and run your business.
          </p>
          <div className="lp-features-grid">
            {[
              {
                icon: '📦',
                title: 'Inventory Tracking',
                desc: 'Add products, set prices, track stock levels. Get warned before you run out.',
              },
              {
                icon: '🧾',
                title: 'Fast Sales Entry',
                desc: 'Record a sale in seconds. Choose cash, partial payment, or debt — all tracked.',
              },
              {
                icon: '💳',
                title: 'Debt Management',
                desc: 'See every customer\'s balance and full payment history at a glance.',
              },
              {
                icon: '⭐',
                title: 'Customer Ratings',
                desc: 'Auto-rates customers as Good, Neutral, or Bad based on repayment behaviour.',
              },
              {
                icon: '📶',
                title: 'Handles Dropped Connections',
                desc: 'If your signal drops while ShopBook is open, sales and payments are kept on your phone and sent automatically when you\'re back online.',
              },
              {
                icon: '👥',
                title: 'Team & Staff',
                desc: 'Invite staff and set what they can and can\'t access. You stay in control.',
              },
              {
                icon: '📊',
                title: 'Financial Reports',
                desc: 'Revenue, expenses, and profit summaries — daily, weekly, or monthly.',
              },
              {
                icon: '🫙',
                title: 'Production Runs',
                desc: 'Log bottling, assembly, or any manufacturing batch — track raw materials consumed and finished goods produced.',
                badge: 'Pro',
              },
              {
                icon: '🔒',
                title: 'Private & Secure',
                desc: 'Your data is isolated to your business. Nobody else can see your records.',
              },
            ].map(({ icon, title, desc, badge }) => (
              <div key={title} className="lp-feature-card">
                <div className="lp-feature-icon">{icon}</div>
                <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {title}
                  {badge && <span style={{ fontSize: '0.65rem', fontWeight: 700, padding: '0.1rem 0.4rem', borderRadius: '4px', background: '#1e3a5f', color: '#60a5fa', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{badge}</span>}
                </h3>
                <p>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ───────────────────────────────────────────────── */}
      <section className="lp-section lp-section-alt" id="how">
        <div className="lp-section-inner">
          <div className="lp-section-label">How it Works</div>
          <h2>Up and running in under 2 minutes</h2>
          <p className="lp-section-sub">Three steps and you're tracking your business like a pro.</p>
          <div className="lp-steps">
            <div className="lp-step">
              <div className="lp-step-num">1</div>
              <div className="lp-step-body">
                <h3>Create your account</h3>
                <p>Sign up with your mobile number or email, enter your business name, and pick your currency. Takes 30 seconds.</p>
              </div>
            </div>
            <div className="lp-step-arrow">→</div>
            <div className="lp-step">
              <div className="lp-step-num">2</div>
              <div className="lp-step-body">
                <h3>Add your products</h3>
                <p>Enter your stock items, prices, and quantities. Add as many as you need — no limits.</p>
              </div>
            </div>
            <div className="lp-step-arrow">→</div>
            <div className="lp-step">
              <div className="lp-step-num">3</div>
              <div className="lp-step-body">
                <h3>Sell, track, collect</h3>
                <p>Record every sale from your phone. See who owes you. Check your totals anytime.</p>
              </div>
            </div>
          </div>
          <div className="lp-steps-cta">
            <button className="lp-btn-primary lp-btn-lg" onClick={() => setModal('register')}>
              Create Free Account
            </button>
          </div>
        </div>
      </section>

      {/* ── PRICING ────────────────────────────────────────────────────── */}
      <section className="lp-section" id="pricing">
        <div className="lp-section-inner">
          <div className="lp-section-label">Pricing</div>
          <h2>Simple, honest pricing</h2>
          <p className="lp-section-sub">Start with a 2-week free trial. Upgrade only when you're ready to grow.</p>
          <div className="lp-billing-toggle" role="group" aria-label="Billing cycle">
            <button
              type="button"
              className={billing === 'monthly' ? 'active' : ''}
              onClick={() => setBilling('monthly')}
            >
              Monthly
            </button>
            <button
              type="button"
              className={billing === 'annual' ? 'active' : ''}
              onClick={() => setBilling('annual')}
            >
              Annual <span className="lp-billing-save">1 month free</span>
            </button>
          </div>
          <div className="lp-pricing-grid">

            <div className="lp-price-card">
              <div className="lp-price-name">Starter</div>
              {priceBlock('starter', 'One business, everything you need to start')}
              <ul className="lp-price-list">
                <li><span className="chk">✓</span> 1 business</li>
                <li><span className="chk">✓</span> Unlimited products</li>
                <li><span className="chk">✓</span> Sales &amp; debt tracking</li>
                <li><span className="chk">✓</span> Customer ratings</li>
                <li><span className="chk">✓</span> Keeps working through dropped signal</li>
                <li><span className="chk">✓</span> 1 device</li>
                <li className="lp-price-no"><span className="x">✗</span> Team members</li>
                <li className="lp-price-no"><span className="x">✗</span> Financial reports</li>
                <li className="lp-price-no"><span className="x">✗</span> Multi-device sync</li>
                <li className="lp-price-no"><span className="x">✗</span> Production runs</li>
                <li className="lp-price-no"><span className="x">✗</span> Multiple businesses</li>
                <li className="lp-price-no"><span className="x">✗</span> Parent company dashboard</li>
                <li className="lp-price-no"><span className="x">✗</span> Fund &amp; capital tracking</li>
              </ul>
              <button className="lp-btn-outline" style={{ width: '100%' }} onClick={() => setModal('register')}>
                Start Free Trial
              </button>
            </div>

            <div className="lp-price-card lp-price-pro">
              <div className="lp-pro-badge">Most Popular</div>
              <div className="lp-price-name">Pro</div>
              {priceBlock('pro', 'One business, with your team')}
              <ul className="lp-price-list">
                <li><span className="chk">✓</span> Everything in Starter</li>
                <li><span className="chk">✓</span> Up to 5 team members</li>
                <li><span className="chk">✓</span> Financial reports</li>
                <li><span className="chk">✓</span> Multi-device sync</li>
                <li><span className="chk">✓</span> Data export (CSV)</li>
                <li><span className="chk">✓</span> Priority support</li>
                <li><span className="chk">✓</span> Custom permissions</li>
                <li><span className="chk">✓</span> Purchase tracking</li>
                <li><span className="chk">✓</span> Production runs</li>
                <li className="lp-price-no"><span className="x">✗</span> Multiple businesses</li>
                <li className="lp-price-no"><span className="x">✗</span> Parent company dashboard</li>
                <li className="lp-price-no"><span className="x">✗</span> Fund &amp; capital tracking</li>
              </ul>
              <button className="lp-btn-primary" style={{ width: '100%' }} onClick={() => setModal('register')}>
                Get Started
              </button>
            </div>

            <div className="lp-price-card lp-price-enterprise">
              <div className="lp-ent-badge">Best for groups</div>
              <div className="lp-price-name">Enterprise</div>
              {priceBlock('enterprise', 'For owners running multiple businesses')}
              <ul className="lp-price-list">
                <li><span className="chk">✓</span> Everything in Pro</li>
                <li className="lp-price-section-label"><span className="chk">✓</span> Multiple businesses</li>
                <li><span className="chk">✓</span> Parent company dashboard</li>
                <li><span className="chk">✓</span> Consolidated P&amp;L &amp; ratios</li>
                <li><span className="chk">✓</span> Fund &amp; capital tracking</li>
                <li><span className="chk">✓</span> Underperformer detection</li>
              </ul>
              <button className="lp-btn-primary" style={{ width: '100%' }} onClick={() => setModal('register')}>
                Get Started
              </button>
            </div>

          </div>
          <p className="lp-pricing-note">
            Every new account starts with a <strong>2-week free trial</strong> of Starter — no card needed.
            <br />Annual plans are billed once a year and include <strong>one month free</strong>.
            <br />Prices shown in Pula (BWP); USD billed at the in-app conversion rate.
          </p>
        </div>
      </section>

      {/* ── BOTTOM CTA ─────────────────────────────────────────────────── */}
      <section className="lp-bottom-cta">
        <h2>Ready to take control of your shop?</h2>
        <p>Join small businesses already using ShopBook to sell smarter and chase less debt.</p>
        <button className="lp-btn-white lp-btn-lg" onClick={() => setModal('register')}>
          Create Your Free Account
        </button>
        <p className="lp-bottom-note">No credit card · Works on any phone · 2-week free trial</p>
      </section>

      {/* ── FOOTER ─────────────────────────────────────────────────────── */}
      <footer className="lp-footer">
        <div className="lp-footer-inner">
          <span className="lp-footer-logo">
            <img src="/emblem-192.png" className="lp-logo-icon lp-logo-icon-sm" alt="ShopBook logo" />
            ShopBook
          </span>
          <span className="lp-footer-tagline">Built for small businesses</span>
          <span className="lp-footer-version">v{APP_VERSION}</span>
          <a className="lp-footer-link" href="/terms">Terms</a>
          <a className="lp-footer-link" href="/privacy">Privacy</a>
          <button className="lp-footer-link" onClick={() => setModal('login')}>Sign In</button>
        </div>
      </footer>

      {/* ── AUTH MODAL ─────────────────────────────────────────────────── */}
      {modal && (
        <div className="lp-overlay" onClick={() => setModal(null)}>
          <div className="lp-modal" onClick={(e) => e.stopPropagation()}>
            <button className="lp-modal-close" onClick={() => setModal(null)} aria-label="Close">✕</button>
            <div className="lp-modal-logo">
              <img src="/emblem-192.png" className="lp-logo-icon" alt="ShopBook logo" />
              <strong>ShopBook</strong>
            </div>
            {(modal === 'login' || modal === 'register') && (
              <div className="lp-modal-tabs">
                <button className={modal === 'login' ? 'active' : ''} onClick={() => setModal('login')}>Sign In</button>
                <button className={modal === 'register' ? 'active' : ''} onClick={() => setModal('register')}>Create Account</button>
              </div>
            )}

            {modal === 'login' && (
              <form onSubmit={handleLogin}>
                {identifierField}
                <div className="form-group">
                  <label>Password</label>
                  <input type="password" value={form.password} onChange={set('password')} required autoComplete="current-password" placeholder="••••••••" />
                </div>
                <p className="lp-modal-forgot">
                  <button type="button" className="lp-link" onClick={() => setModal('forgot')}>Forgot password?</button>
                </p>
                <button type="submit" className="lp-btn-primary" style={{ width: '100%', marginTop: '0.5rem' }} disabled={loading}>
                  {loading ? 'Signing in…' : 'Sign In'}
                </button>
                <p className="lp-modal-switch">
                  No account? <button type="button" className="lp-link" onClick={() => setModal('register')}>Create one free</button>
                </p>
              </form>
            )}

            {modal === 'forgot' && (
              <form onSubmit={handleForgot}>
                <h3 className="lp-modal-heading">Reset your password</h3>
                <p className="lp-modal-desc">
                  Enter the mobile number or email you sign in with and we'll send you a
                  6-digit reset code — by SMS to a mobile number, or by email.
                </p>
                {identifierField}
                <button type="submit" className="lp-btn-primary" style={{ width: '100%', marginTop: '0.5rem' }} disabled={loading}>
                  {loading ? 'Sending…' : 'Send reset code'}
                </button>
                <p className="lp-modal-switch">
                  Remembered it? <button type="button" className="lp-link" onClick={() => setModal('login')}>Back to sign in</button>
                </p>
              </form>
            )}

            {modal === 'reset' && (
              <form onSubmit={handleReset}>
                <h3 className="lp-modal-heading">Enter reset code</h3>
                <p className="lp-modal-desc">
                  We sent a 6-digit code {loginMethod === 'email' ? 'to' : 'by SMS to'}{' '}
                  <strong>{loginMethod === 'email' ? (form.email || 'your email') : `+${fullPhone()}`}</strong>. It expires in 10 minutes.
                </p>
                <div className="form-group">
                  <label>6-digit code</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={form.reset_code}
                    onChange={(e) => setForm(f => ({ ...f, reset_code: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                    required
                    placeholder="123456"
                  />
                </div>
                <div className="form-group">
                  <label>New password</label>
                  <input type="password" value={form.new_password} onChange={set('new_password')} required autoComplete="new-password" placeholder="Min. 8 characters" />
                </div>
                <button type="submit" className="lp-btn-primary" style={{ width: '100%', marginTop: '0.5rem' }} disabled={loading}>
                  {loading ? 'Resetting…' : 'Reset password'}
                </button>
                <p className="lp-modal-switch">
                  Didn't get a code? <button type="button" className="lp-link" onClick={() => setModal('forgot')}>Send again</button>
                </p>
              </form>
            )}

            {modal === 'register' && (
              <form onSubmit={handleRegister}>
                <div className="form-group">
                  <label>Your name</label>
                  <input type="text" value={form.name} onChange={set('name')} required placeholder="e.g. Bwalya Phiri" />
                </div>
                <div className="form-group">
                  <label>Email <span style={{ fontWeight: 400, opacity: 0.7 }}>(optional)</span></label>
                  <input type="email" value={form.email} onChange={set('email')} autoComplete="off" placeholder="you@example.com" />
                </div>
                <div className="form-group">
                  <label>Password</label>
                  <input type="password" value={form.password} onChange={set('password')} required autoComplete="new-password" placeholder="Min. 8 characters" />
                </div>
                <div className="form-group">
                  <label>Confirm password</label>
                  <input type="password" value={form.confirm_password} onChange={set('confirm_password')} required autoComplete="new-password" placeholder="Re-enter password" />
                </div>
                <div className="form-group">
                  <label>Business name</label>
                  <input type="text" value={form.business_name} onChange={set('business_name')} required placeholder="e.g. Mama Phiri General Store" />
                </div>
                <div className="form-group">
                  <label>Country</label>
                  <select value={form.country_code} onChange={setCountry}>
                    {COUNTRIES.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.flag} {c.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Mobile number</label>
                  <div className="lp-phone-row">
                    <span className="lp-dial-code">{selectedCountry.flag} {selectedCountry.dial}</span>
                    <input
                      type="tel"
                      value={form.phone_number}
                      onChange={set('phone_number')}
                      placeholder="71 234 567"
                      required
                    />
                  </div>
                </div>
                <div className="lp-currency-note">
                  <span>Currency will be set to</span>
                  <strong>{selectedCountry.currency} — {selectedCountry.currencyName}</strong>
                </div>
                <label style={{ display: 'flex', gap: '0.55rem', alignItems: 'flex-start', fontSize: '0.85rem', marginTop: '0.75rem', cursor: 'pointer', textAlign: 'left' }}>
                  <input type="checkbox" checked={acceptedTerms} onChange={e => setAcceptedTerms(e.target.checked)}
                    required style={{ width: 'auto', marginTop: '0.2rem', flexShrink: 0 }} />
                  <span>
                    I have read and agree to the{' '}
                    <a href="/terms" target="_blank" rel="noreferrer">Terms of Service</a> and the{' '}
                    <a href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>.
                  </span>
                </label>
                <button type="submit" className="lp-btn-primary" style={{ width: '100%', marginTop: '0.75rem' }} disabled={loading || !acceptedTerms}>
                  {loading ? 'Creating account…' : 'Create Free Account'}
                </button>
                <p className="lp-modal-switch">
                  Already have an account? <button type="button" className="lp-link" onClick={() => setModal('login')}>Sign in</button>
                </p>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
